import { animate, motion, useMotionValue } from 'framer-motion';
import { Duration } from 'luxon';
import React, { useEffect, useMemo, useRef } from 'react';
import { customDurationFormat } from 'utils/duration-helper';
import AnimatedNumber from './AnimatedNumber';

export type DurationValueTypes = 'seconds' | 'minutes' | 'hours';
export interface Props {
  animated?: boolean;
  className?: string;
  format?: string;
  highlightChange?: boolean;
  style?: React.CSSProperties;
  trim?: 'right' | 'both' | boolean;
  stopTrim?: string;
  trunc?: boolean;
  value?: number | string | null;
  valueType?: DurationValueTypes;

  /**
   * If provided, this will be displayed instead if the value is zero.
   */
  zeroPlaceholder?: React.ReactNode;
}

type FormatFn = (value: number | string | null | undefined) => string;
const DynamicDurationFormat: React.FC<Props & { formatFn: FormatFn }> = ({
  value,
  animated,
  style,
  className,
  highlightChange,
  formatFn,
}) => {
  const shouldAnimate = animated && !isNaN(+(value || 0));

  const lastValue = useRef(value);
  const highlightValue = useMotionValue(1);

  useEffect(() => {
    const changed = value !== lastValue.current && value != null && !!lastValue.current;
    lastValue.current = value;
    if (highlightChange && changed) {
      const controls = animate(highlightValue, [1, 1.3, 1], {
        type: 'tween',
        duration: 0.4,
      });

      return controls.stop;
    }
  }, [value, highlightChange, highlightValue]);

  return (
    <motion.span
      style={{ ...style, display: 'inline-block', scale: highlightValue }}
      className={className}
      data-testid="duration-format"
    >
      {shouldAnimate ? <AnimatedNumber value={+(value || 0)} format={formatFn} /> : formatFn(value)}
    </motion.span>
  );
};

const DurationFormat = React.memo<Props>(props => {
  const {
    trunc = true, // Don't round up, ie: 2.75minutes = 2 minutes
    value = 0,
    format = 'h:mm',
    valueType = 'minutes',
    trim = false, // see NB note at top: Should trim missing values from the right
    stopTrim,
    animated,
    className,
    highlightChange,
    zeroPlaceholder,
  } = props;
  const style = useMemo(() => ({ ...props.style, display: 'inline-block' }), [props.style]);

  if (value === 0 && zeroPlaceholder) {
    return React.isValidElement(zeroPlaceholder) ? (
      zeroPlaceholder
    ) : (
      <span style={style} className={className} data-testid="duration-format">
        {zeroPlaceholder}
      </span>
    );
  }
  // replace Moment literals with Luxon ones
  const parsedFormat = format.replace(/[[\]]/g, "'");

  const formatFn = (value: any = 0) => {
    const parsed = Duration.fromObject({ [valueType]: value });

    return customDurationFormat({
      value: parsed.as('seconds'),
      valueType: 'seconds',
      format: parsedFormat,
      trim,
      stopTrim,
      trunc,
    });
  };

  const shouldAnimate = animated && !isNaN(+(value || 0));
  const isDynamic = shouldAnimate || highlightChange;

  // This split is done as a performance improvement on slow machines. Fast machines can handle the
  // extra indirection, but slow machines struggle with lots of instances of motion + animated.
  return isDynamic ? (
    <DynamicDurationFormat {...props} formatFn={formatFn} />
  ) : (
    <span style={style} className={className} data-testid="duration-format">
      {formatFn(value)}
    </span>
  );
});

DurationFormat.displayName = 'DurationFormat';

export default DurationFormat;
